<template>
  <!-- Scale-to-fit (contain): when widgetWidth/Height are set, the iframe is rendered at that size and the wrapper
       is scaled down to fit inside the host without cropping; letterboxing uses bg-neutral-400. -->
  <div ref="hostRef" class="w-full h-full relative bg-neutral-400">
    <!-- Loading placeholder: visible only when content is hidden (Android, waiting for READY or iframe load). Avoids harsh black screen. -->
    <div
      v-show="!showContent"
      class="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-neutral-400 transition-opacity duration-150"
      aria-hidden="true"
    >
      <Loader2
        class="animate-spin text-neutral-600 shrink-0"
        style="width: 1.5rem; height: 1.5rem;"
      />
      <span class="text-xs text-neutral-600">Loading…</span>
    </div>
    <!-- On Android, hide iframe content until widget has sent READY (avoids visible loading state when slot comes into view). -->
    <div
      class="w-full h-full relative transition-opacity duration-150 z-[1] overflow-hidden"
      :class="showContent ? 'opacity-100' : 'opacity-0'"
    >
      <div
        v-if="widgetWidth != null && widgetHeight != null && widgetWidth > 0 && widgetHeight > 0"
        class="absolute origin-top-left"
        :style="scaleWrapperStyle"
      >
        <iframe
          ref="iframeRef"
          class="border-0 block outline-none"
          :src="effectiveSrc"
          :style="iframeFixedStyle"
          loading="eager"
          sandbox="allow-scripts allow-same-origin"
          @load="onIframeLoad"
        />
      </div>
      <iframe
        v-else
        ref="iframeRef"
        class="w-full h-full border-0 block outline-none"
        :src="effectiveSrc"
        loading="eager"
        sandbox="allow-scripts allow-same-origin"
        @load="onIframeLoad"
      />
    </div>
    <!-- Transparent overlay: forward single clicks into the widget; skip forward after status triple-tap. -->
    <div
      class="absolute inset-0 z-[2] cursor-inherit"
      data-widget-click-overlay
      @click="onOverlayClick"
    />
  </div>
</template>

<script lang="ts" setup>
import type { IWidgetMessageEnvelope, IWidgetStateRequestPayload, IWidgetStateResponsePayload } from '@lucimark/shared/models/widgetRuntimeContract'
import {
  createHostToWidgetMessage,
  isWidgetProtocolMessage,
  WIDGET_MSG_CONFIG_UPDATE,
  WIDGET_MSG_FRAME_ENTER,
  WIDGET_MSG_FRAME_EXIT,
  WIDGET_MSG_INIT,
  WIDGET_MSG_STATE_RESPONSE
} from '@lucimark/shared/models/widgetRuntimeContract'
import { Loader2 } from 'lucide-vue-next'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { isAndroidWebView } from '@/helpers/platform'
import { consumeSuppressClickForwardAfterTripleTap } from '@/helpers/statusOverlayTripleTap'

const props = withDefaults(
  defineProps<{
    src: string
    config?: Record<string, unknown>
    show?: boolean
    /** Asset URN for this iframe; when set, iframe load is reported so playback treats the asset as ready. */
    assetUrn?: string
    /** True when this iframe is the singleton instance (one per campaign). Used for debug logging. */
    singletonInstance?: boolean
    /** Optional label for debug log when this iframe is shown for a frame (e.g. frame id or seq). */
    debugFrameLabel?: string
    /** Widget native width (from package.json lucimark). When set with widgetHeight, iframe is rendered at this size and scaled to fit the host. */
    widgetWidth?: number
    /** Widget native height (from package.json lucimark). When set with widgetWidth, iframe is rendered at this size and scaled to fit the host. */
    widgetHeight?: number
  }>(),
  { config: () => ({}), show: false, singletonInstance: false, debugFrameLabel: '' }
)

const emit = defineEmits<{ iframeLoaded: [urn: string], widgetReady: [] }>()

/** Use about:blank when src is empty so the iframe always fires load (avoids stuck "widget(s) loading"). */
const effectiveSrc = computed(() => (props.src && props.src.trim()) || 'about:blank')

/** URL widgets (assetUrn contains :url-) load an external page and never send READY; on Android show them after iframe load. */
const isUrlWidget = computed(() => (props.assetUrn ?? '').includes(':url-'))
const urlWidgetLoaded = ref(false)

/**
 * On Android, when iframe load fires but READY never arrives (e.g. cached document, no second DOMContentLoaded),
 * show content after a short delay so the spinner does not stay forever. Reset when src changes.
 */
const loadFallbackShowContent = ref(false)
let loadFallbackTimeoutId: ReturnType<typeof setTimeout> | null = null

/**
 * True after we have sent INIT in response to READY for the current document.
 * INIT is sent exclusively in response to READY (pure pull model) — never proactively pushed.
 * - When true: ignore further READY messages (no double INIT); allow FRAME_* and CONFIG_UPDATE.
 * - Reset when props.src changes so the new page's READY gets a fresh INIT.
 * - In onIframeLoad we do NOT reset when this is already true: READY is typically sent on
 *   DOMContentLoaded, which fires before the iframe load event; resetting here would clear the
 *   flag after we had already sent INIT and allow a second INIT on duplicate READY.
 */
const hasSentInitAfterReady = ref(false)

/** On Android, hide iframe content until READY (tgz) or load (URL widget); on web always show. Avoids visible loading state when slot comes into view on WebView. */
const showContent = computed(() => !isAndroidWebView() || hasSentInitAfterReady.value || (isUrlWidget.value && urlWidgetLoaded.value) || loadFallbackShowContent.value)

const iframeRef = ref<HTMLIFrameElement | null>(null)
const hostRef = ref<HTMLDivElement | null>(null)
const hostSize = ref({ width: 0, height: 0 })

/**
 * Scale factor so the widget (at widgetWidth×widgetHeight) fits inside the host ("contain").
 * Letterboxing is visible as host bg-neutral-400. Cap at 1 to never upscale.
 */
const scale = computed(() => {
  const w = props.widgetWidth ?? 0
  const h = props.widgetHeight ?? 0
  const cw = hostSize.value.width
  const ch = hostSize.value.height
  if (w <= 0 || h <= 0 || cw <= 0 || ch <= 0) { return 1 }
  return Math.min(cw / w, ch / h, 1)
})

const scaleWrapperStyle = computed(() => {
  const w = props.widgetWidth ?? 0
  const h = props.widgetHeight ?? 0
  if (w <= 0 || h <= 0) { return {} }
  const s = scale.value
  const scaledW = w * s
  const scaledH = h * s
  const cw = hostSize.value.width
  const ch = hostSize.value.height
  const left = cw > 0 ? (cw - scaledW) / 2 : 0
  const top = ch > 0 ? (ch - scaledH) / 2 : 0
  return {
    width: `${w}px`,
    height: `${h}px`,
    left: `${left}px`,
    top: `${top}px`,
    transform: `scale(${s})`
  }
})

const iframeFixedStyle = computed(() => {
  const w = props.widgetWidth ?? 0
  const h = props.widgetHeight ?? 0
  if (w <= 0 || h <= 0) { return {} }
  return { width: `${w}px`, height: `${h}px` }
})

function onOverlayClick(e: MouseEvent) {
  e.preventDefault()
  e.stopPropagation()
  // Window capture-phase triple-tap already toggled status; do not synthesize a widget click.
  if (consumeSuppressClickForwardAfterTripleTap()) {
    return
  }
  const iframe = iframeRef.value
  const doc = iframe?.contentWindow?.document
  if (!doc?.defaultView) { return }
  try {
    const rect = iframe!.getBoundingClientRect()
    const s = scale.value
    const relX = (e.clientX - rect.left) / s
    const relY = (e.clientY - rect.top) / s
    const el = doc.elementFromPoint(relX, relY)
    if (el) {
      el.dispatchEvent(new MouseEvent(e.type, {
        bubbles: true,
        cancelable: true,
        view: doc.defaultView,
        clientX: relX,
        clientY: relY,
        screenX: e.screenX,
        screenY: e.screenY,
        button: e.button,
        buttons: e.buttons,
        relatedTarget: e.relatedTarget
      }))
    }
  }
  catch {
    // Cross-origin or unavailable
  }
}
/** JSON snapshot of the last config sent via INIT or CONFIG_UPDATE; used to skip no-op sends. */
const lastSentConfigJson = ref<string | null>(null)
/** Tracks last frame visibility state we sent (ENTER = in, EXIT = out) to avoid duplicate same event. */
const lastFrameVisibility = ref<'in' | 'out' | null>(null)

function postToWidget(msg: IWidgetMessageEnvelope) {
  const iframe = iframeRef.value
  if (!iframe?.contentWindow) { return }
  try {
    iframe.contentWindow.postMessage(JSON.parse(JSON.stringify(msg)), '*')
  }
  catch (e) {
    console.warn('[WidgetIframeHost] postMessage failed', e)
  }
}

function sendConfig(cfg: Record<string, unknown>) {
  postToWidget(createHostToWidgetMessage(WIDGET_MSG_CONFIG_UPDATE, { config: cfg }))
}

function clearLoadFallbackTimeout() {
  if (loadFallbackTimeoutId != null) {
    clearTimeout(loadFallbackTimeoutId)
    loadFallbackTimeoutId = null
  }
}

function onIframeLoad() {
  // Only reset when we have not yet sent INIT (load fired before READY). If we already sent INIT,
  // READY likely arrived first (e.g. on DOMContentLoaded), so do not clear the flag — that would
  // allow a duplicate READY to trigger a second INIT.
  if (!hasSentInitAfterReady.value) {
    lastSentConfigJson.value = null
  }
  if (isUrlWidget.value) {
    urlWidgetLoaded.value = true
    // URL widgets never send READY; emit widgetReady on load so two-slot (and other hosts) get a consistent ready signal.
    emit('widgetReady')
  }
  if (props.assetUrn) {
    emit('iframeLoaded', props.assetUrn)
  }
  /**
   * For tgz widgets, if READY never arrives (e.g. cache, script error, or message lost), show content
   * after a short delay so the gray loading state doesn't stay forever. Skip hard-failure `about:blank`
   * (broken package / empty src) — painting that shell is not a protocol-ready degrade path.
   */
  const src = (props.src && props.src.trim()) || ''
  if (!isUrlWidget.value && !hasSentInitAfterReady.value && src !== '' && src !== 'about:blank') {
    clearLoadFallbackTimeout()
    loadFallbackTimeoutId = setTimeout(() => {
      loadFallbackTimeoutId = null
      if (!hasSentInitAfterReady.value) {
        loadFallbackShowContent.value = true
      }
    }, 800)
  }
}

function handleWidgetMessage(event: MessageEvent) {
  if (event.source !== iframeRef.value?.contentWindow) { return }
  if (!isWidgetProtocolMessage(event.data)) { return }

  const env = event.data as IWidgetMessageEnvelope
  switch (env.type) {
    case 'READY': {
      if (hasSentInitAfterReady.value) { break }
      clearLoadFallbackTimeout()
      hasSentInitAfterReady.value = true
      const cfg = props.config ?? {}
      postToWidget(createHostToWidgetMessage(WIDGET_MSG_INIT, { config: cfg }))
      lastSentConfigJson.value = JSON.stringify(cfg)
      if (props.show) {
        if (lastFrameVisibility.value !== 'in') {
          lastFrameVisibility.value = 'in'
          postToWidget(createHostToWidgetMessage(WIDGET_MSG_FRAME_ENTER))
        }
      }
      else {
        lastFrameVisibility.value = 'out'
      }
      emit('widgetReady')
      break
    }
    case 'STATE_GET':
    case 'STATE_SET':
    case 'STATE_CLEAR': {
      const payload = env.payload as IWidgetStateRequestPayload | undefined
      const requestId = payload?.requestId ?? env.requestId
      if (requestId) {
        // Stub: respond with success: false until state API is implemented
        postToWidget(
          createHostToWidgetMessage(WIDGET_MSG_STATE_RESPONSE, {
            requestId,
            success: false,
            error: 'State API not yet implemented'
          } as IWidgetStateResponsePayload)
        )
      }
      break
    }
    default:
      break
  }
}

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  window.addEventListener('message', handleWidgetMessage)
  // Only observe when widget dimensions are set (scale-to-fit path). Avoids cost for non-widget or legacy widgets.
  const el = hostRef.value
  if (el && props.widgetWidth != null && props.widgetHeight != null) {
    resizeObserver = new ResizeObserver(([entry]) => {
      const target = entry?.target as HTMLElement
      if (target) {
        hostSize.value = { width: target.clientWidth, height: target.clientHeight }
      }
    })
    resizeObserver.observe(el)
    hostSize.value = { width: el.clientWidth, height: el.clientHeight }
  }
})

onBeforeUnmount(() => {
  clearLoadFallbackTimeout()
  resizeObserver?.disconnect()
  resizeObserver = null
  window.removeEventListener('message', handleWidgetMessage)
})

// When the host assigns a new widget URL, reset so the new page's READY gets a fresh INIT.
watch(
  () => props.src,
  () => {
    clearLoadFallbackTimeout()
    hasSentInitAfterReady.value = false
    urlWidgetLoaded.value = false
    loadFallbackShowContent.value = false
    lastSentConfigJson.value = null
    lastFrameVisibility.value = null
  }
)

// For per-section iframes (WidgetTwoSlotLayer), we react to show for FRAME_ENTER/EXIT. (Legacy singleton layer no longer used.)
watch(
  () => props.show,
  (show) => {
    if (!hasSentInitAfterReady.value) { return }
    if (props.singletonInstance) { return }
    if (show) {
      if (lastFrameVisibility.value !== 'in') {
        lastFrameVisibility.value = 'in'
        postToWidget(createHostToWidgetMessage(WIDGET_MSG_FRAME_ENTER))
      }
    }
    else {
      if (lastFrameVisibility.value !== 'out') {
        lastFrameVisibility.value = 'out'
        postToWidget(createHostToWidgetMessage(WIDGET_MSG_FRAME_EXIT))
      }
    }
  }
)

watch(
  () => props.config,
  (config) => {
    if (!hasSentInitAfterReady.value) { return }
    const json = JSON.stringify(config ?? {})
    if (json === lastSentConfigJson.value) { return }
    lastSentConfigJson.value = json
    sendConfig(config ?? {})
  },
  { deep: true }
)

/** Legacy: was called by deprecated SingletonWidgetLayer. Kept for API compatibility; WidgetTwoSlotLayer drives ENTER/EXIT via show. */
function sendFrameEnter(): void {
  if (!hasSentInitAfterReady.value) { return }
  if (lastFrameVisibility.value === 'in') { return }
  lastFrameVisibility.value = 'in'
  postToWidget(createHostToWidgetMessage(WIDGET_MSG_FRAME_ENTER))
}

/** Legacy: was called by deprecated SingletonWidgetLayer. Kept for API compatibility. */
function sendFrameExit(): void {
  if (!hasSentInitAfterReady.value) { return }
  if (lastFrameVisibility.value === 'out') { return }
  lastFrameVisibility.value = 'out'
  postToWidget(createHostToWidgetMessage(WIDGET_MSG_FRAME_EXIT))
}

/**
 * Legacy: was called by deprecated SingletonWidgetLayer for frame transitions. Kept for API compatibility.
 */
function signalFrameTransition(): void {
  if (!hasSentInitAfterReady.value) { return }
  lastFrameVisibility.value = 'out'
  postToWidget(createHostToWidgetMessage(WIDGET_MSG_FRAME_EXIT))
  lastFrameVisibility.value = 'in'
  postToWidget(createHostToWidgetMessage(WIDGET_MSG_FRAME_ENTER))
}

defineExpose({ sendFrameEnter, sendFrameExit, signalFrameTransition })
</script>
